import { create } from 'zustand';
import { isLocale, type Locale, type ZagruProblem } from '@zagru/platform';

const LOCALE_KEY = 'zagru.locale';
export const DENSITY_CHOSEN_KEY = 'zagru.density.chosen';

function readStorage(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

export interface LocaleState {
  /** Locale explicitly chosen by the user (persisted); `null` follows the session user's locale. */
  chosen: Locale | null;
  setChosen(locale: Locale | null): void;
}

/** User-chosen UI locale; wins over the session user's locale. */
export const localeStore = create<LocaleState>()((set) => ({
  chosen: (() => {
    const stored = readStorage(LOCALE_KEY);
    return isLocale(stored) ? stored : null;
  })(),
  setChosen: (locale) => {
    writeStorage(LOCALE_KEY, locale);
    set({ chosen: locale });
  },
}));

const NAV_COLLAPSED_KEY = 'zagru.shell.nav.collapsed';

export interface NavState {
  /** Icon-only side navigation (72px) instead of icon+label (248px). Remembered per user. */
  collapsed: boolean;
  setCollapsed(collapsed: boolean): void;
}

/** Side-navigation collapse state, persisted so the shell opens the way the user left it. */
export const navStore = create<NavState>()((set) => ({
  collapsed: readStorage(NAV_COLLAPSED_KEY) === '1',
  setCollapsed: (collapsed) => {
    writeStorage(NAV_COLLAPSED_KEY, collapsed ? '1' : '0');
    set({ collapsed });
  },
}));

export interface LastProblem {
  status: number;
  code?: string;
  title: string;
  correlationId?: string;
  at: number;
}

export interface ProblemState {
  last: LastProblem | null;
  record(problem: ZagruProblem): void;
  clear(): void;
}

/** Keeps the most recent API problem so the status bar can surface its correlation id. */
export const problemStore = create<ProblemState>()((set) => ({
  last: null,
  record: (problem) =>
    set({ last: { status: problem.status, code: problem.code, title: problem.title, correlationId: problem.correlationId, at: Date.now() } }),
  clear: () => set({ last: null }),
}));

/** Whether the user picked a density explicitly (otherwise the role profile default applies). */
export function isDensityChosen(): boolean {
  return readStorage(DENSITY_CHOSEN_KEY) === '1';
}

export function markDensityChosen(): void {
  writeStorage(DENSITY_CHOSEN_KEY, '1');
}

/** Return-to path captured by the OIDC callback, consumed by the callback page. */
let pendingReturnTo: string | null = null;
export function setPendingReturnTo(path: string | null): void {
  pendingReturnTo = path;
}
export function consumePendingReturnTo(): string | null {
  const value = pendingReturnTo;
  pendingReturnTo = null;
  return value;
}

/**
 * Only same-origin, absolute in-app paths are accepted as return targets: no protocol-relative (`//host`) or
 * backslash tricks, and never the auth routes themselves (which would loop).
 */
export function safeReturnTo(value: string | null | undefined, fallback: string): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return fallback;
  if (/^\/(login|logout|auth)(\/|\?|#|$)/.test(value)) return fallback;
  if (/^\/dashboard(\/|\?|#|$)/.test(value)) return '/today';
  if (/^\/dashboards(\/|\?|#|$)/.test(value)) return '/reports/dashboards';
  if (/^\/reports\/dashboards\/[^/?#]+(?:[/?#].*)?$/.test(value)) return '/reports/dashboards';
  return value;
}

export interface VersionState {
  /** A newer build was detected (a lazily loaded chunk no longer exists); the shell offers a reload. */
  newVersionAvailable: boolean;
  markNewVersion(): void;
}

/** Whether the running build is stale, for the shell's new-version banner. */
export const versionStore = create<VersionState>()((set) => ({
  newVersionAvailable: false,
  markNewVersion: () => set({ newVersionAvailable: true }),
}));
